import {AbstractControl, ValidationErrors, ValidatorFn, FormGroup} from '@angular/forms';

/**
 * Validateur : La date sélectionnée doit être aujourd'hui ou une date future.
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
export function dateApresAujourdhui(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const date = new Date(control.value);
		const aujourdhui = new Date();

		return date.valueOf() >= aujourdhui.valueOf() ? null : { dateInvalide: true };
	};
}

/**
 * Validateur : Vérifie que le départ est avant l'arrivée
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
function departAvantArrivee(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const dateDepart = control.get('dateDepart')?.value;
		const dateArrivee = control.get('dateArrivee')?.value;

		if (!dateDepart || !dateArrivee) {
			return null;
		}

		return dateDepart.valueOf() < dateArrivee.valueOf() ? null : { arriveeAvantDepart: true };
	}
}

/**
 * Validateur : Le lieu de départ et le lieu d'arrivée doivent être différents.
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
function lieuxValidation(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const lieuDepart = control.get('lieuDepart')?.value;
		const adresseDepart = control.get('adresseDepart')?.value;

		const lieuArrivee = control.get('lieuArrivee')?.value;
		const adresseArrivee = control.get('adresseArrivee')?.value;

		if (!lieuDepart || !lieuArrivee) {
			return null;
		}

		const lieuxDifferents = lieuDepart !== lieuArrivee;
		const adresseDepartNormalisee = adresseDepart ?? '';
		const adresseArriveeNormalisee = adresseArrivee ?? '';
		const adressesVides = adresseDepartNormalisee === '' && adresseArriveeNormalisee === '';
		const adressesVidesOuDifferentes = adressesVides || adresseDepartNormalisee !== adresseArriveeNormalisee;

		return lieuxDifferents && adressesVidesOuDifferentes ? null : { lieuxIdentiques: true };
	}
}

/**
 * Validateur global pour le formulaire de trajet.
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
export function trajetValidation(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const lieuxErreurs = lieuxValidation()(control);
		const datesErreurs = departAvantArrivee()(control);

		const errors = { ...lieuxErreurs ?? {}, ...datesErreurs ?? {} };
		return Object.keys(errors).length > 0 ? errors : null;
	};
}

/**
 * Validateur pour vérifier que le minimum de places est inférieur ou égal au maximum de places.
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
function miniMaxiNombreDePlacesValidation(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const minimum = control.get('minimumPlaces')?.value;
		const maximum = control.get('maximumPlaces')?.value;

		if (!minimum || !maximum) {
			return null;
		}

		return minimum <= maximum ? null : { minimumSuperieurMaximum: true };
	}
}

/**
 * Validateur pour le filtrage des trajets.
 *
 * @returns {ValidatorFn} la fonction de validation.
 */
export function filtrageTrajetValidation(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const miniMaxiNombreDePlacesErreurs = miniMaxiNombreDePlacesValidation()(control);

		const errors = { ...miniMaxiNombreDePlacesErreurs ?? {} };
		return Object.keys(errors).length > 0 ? errors : null;
	}
}